import { useState } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalCard } from '@ovasabi/ui-minimal'
import { PageHeader, Screen } from '../../components/chow'
import { Button } from '../../components/ui/Button'
import { commands, isLiveRuntime } from '../../lib/runtime'
import { useBackNav, useChooseChow } from './ChooseChowContext'
import { useAuth } from './auth/AuthContext'
import { ALLERGEN_CATEGORIES, allergenLabel } from './allergens'
import { setMyAllergies, useMyAllergies } from './projections'

/**
 * Health & allergies — a dedicated screen reached from the profile menu (its own
 * "tab", consistent with Orders / Wallet). The eater picks allergen categories to
 * avoid; they are saved on the profile using the same vocabulary as dish
 * allergens, so a dish page can warn when a dish contains something to avoid.
 */
const Style = {
  Intro: styled.p`
    margin: 0 0 ${minimalVars.space.sm};
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: 1.4;
  `,
  Summary: styled.div`
    margin-bottom: ${minimalVars.space.sm};
    font-size: ${minimalVars.typography.captionSize};
    color: ${minimalVars.color.textTertiary};
    strong {
      color: ${minimalVars.color.textPrimary};
    }
  `,
  Chips: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  `,
  Chip: styled.button<{ $active: boolean }>`
    cursor: pointer;
    border: 1px solid
      ${({ $active }) => ($active ? minimalVars.color.brand : minimalVars.color.borderSubtle)};
    background: ${({ $active }) => ($active ? minimalVars.color.brandSoft : 'transparent')};
    color: ${({ $active }) => ($active ? minimalVars.color.brand : minimalVars.color.textSecondary)};
    border-radius: ${minimalVars.radius.pill};
    font: inherit;
    font-size: ${minimalVars.typography.captionSize};
    font-weight: ${minimalVars.typography.weightSemibold};
    padding: 8px 14px;
  `,
  Foot: styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${minimalVars.space.xs};
    margin-top: ${minimalVars.space.sm};
  `,
  Note: styled.span<{ $error?: boolean }>`
    margin-right: auto;
    font-size: ${minimalVars.typography.captionSize};
    color: ${({ $error }) => ($error ? minimalVars.color.danger : minimalVars.color.textTertiary)};
  `,
}

export const AllergiesPage = () => {
  const back = useBackNav()
  const { profileId } = useChooseChow()
  const { user } = useAuth()
  const saved = useMyAllergies(profileId)
  const [draft, setDraft] = useState<ReadonlySet<string> | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<'saved' | 'error' | ''>('')

  const current: ReadonlySet<string> = draft ?? new Set(saved)
  const dirty = draft !== null

  const toggle = (slug: string) => {
    const next = new Set(current)
    if (next.has(slug)) next.delete(slug)
    else next.add(slug)
    setDraft(next)
    setNotice('')
  }

  const save = async () => {
    if (busy) return
    setBusy(true)
    setNotice('')
    const allergies = [...current]
    try {
      if (isLiveRuntime()) {
        await commands.profile.upsertProfile({
          profile_id: profileId,
          display_name: user?.displayName ?? 'Eater',
          allergies,
        })
        // Allergies are read through the authorized profile command, not the
        // projection, so nothing streams the new value back. Publish what the
        // server just accepted; every consumer of useMyAllergies (this page and
        // the dish warnings) reads the same cache.
        setMyAllergies(profileId, allergies)
      }
      setDraft(null)
      setNotice('saved')
    } catch {
      setNotice('error')
    } finally {
      setBusy(false)
    }
  }

  const selected = [...current]
  const summary =
    selected.length === 0 ? 'None set' : selected.map((slug) => allergenLabel(slug)).join(', ')

  return (
    <Screen
      header={
        <PageHeader
          title="Health & allergies"
          accentDot
          onBack={back}
        />
      }
    >
      <MinimalCard padding="md" enter={false}>
        <Style.Intro>
          Tell us what to avoid. We flag any dish that lists one of these allergens, across every
          kitchen.
        </Style.Intro>
        <Style.Summary>
          Currently avoiding · <strong>{summary}</strong>
        </Style.Summary>
        <Style.Chips>
          {ALLERGEN_CATEGORIES.map((cat) => {
            const active = current.has(cat.slug)
            return (
              <Style.Chip
                key={cat.slug}
                type="button"
                $active={active}
                aria-pressed={active}
                onClick={() => toggle(cat.slug)}
              >
                <span aria-hidden>{cat.glyph}</span> {cat.label}
              </Style.Chip>
            )
          })}
        </Style.Chips>
        <Style.Foot>
          {notice === 'saved' && <Style.Note>Preferences saved.</Style.Note>}
          {notice === 'error' && <Style.Note $error>Could not save — try again.</Style.Note>}
          <Button size="sm" onClick={save} disabled={busy || !dirty}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </Style.Foot>
      </MinimalCard>
    </Screen>
  )
}
